// =================================================================
// Recommendation engine
//
// Given a resident's level, goals, and assessment domain breakdown,
// produces today's recommended games and the reasons behind them.
//
// All functions here are pure (no I/O, no Firebase). They take a
// snapshot of the resident's state and return a recommended set.
// The dashboard renders the result; this file doesn't touch the DOM.
//
// MODEL CHANGE (v2026.06.04.01):
//   Games now declare a `goals` list (keys A-E) instead of a
//   primary/secondary domain pair. The resident's `goals` are the
//   same A-E keys. So goal matching happens directly in goal-space:
//   the FIRST goal in a game's list is primary (+2), the rest are
//   secondary (+1).
//
//   The assessment "weak domain" boost still works in DOMAIN-space
//   (the breakdown is keyed by domain). We bridge the two spaces via
//   each goal's `domain` field: a game's goal maps to a domain, and
//   if that domain is weak the game gets a boost. Reasons carry both
//   the goal key and its domain so the dashboard can render a label.
//
// Scoring model:
//   primary goal matches a resident goal:    +2
//   secondary goal matches a resident goal:  +1
//   game covers a "weak" domain (<50%):      +1 boost (primary)
//                                            +0.5 boost (secondary)
//
// Sort descending by score, break ties by catalog order (a stable
// curation choice), then apply daily rotation to keep the surface
// fresh without sacrificing the "matched to me" feel.
// =================================================================

import {
  GAMES_LIST,
  gamesForLevel,
  GOALS,
  domainForGoal,
} from '../data/gamesCatalog.js';

const WEAK_DOMAIN_THRESHOLD = 0.5;  // <50% earned counts as weak

// Reverse lookup: domain key -> goal key. Built once. Used to convert
// a weak DOMAIN (from the assessment breakdown) into the resident
// goal it corresponds to, so weak-domain boosts work in goal-space.
const GOAL_BY_DOMAIN = new Map(GOALS.map((g) => [g.domain, g.key]));

/**
 * Identify domains where the resident scored below the weak
 * threshold. Returns a Set of domain keys.
 *
 * Domains not present in the breakdown are treated as "unknown" —
 * neither weak nor strong. They don't get the boost, but they don't
 * get penalized either.
 */
export function weakDomains(domainBreakdown) {
  if (!domainBreakdown) return new Set();
  const weak = new Set();
  Object.entries(domainBreakdown).forEach(([domain, scores]) => {
    if (!scores) return;
    const { earned, possible } = scores;
    if (possible > 0 && earned / possible < WEAK_DOMAIN_THRESHOLD) {
      weak.add(domain);
    }
  });
  return weak;
}

/**
 * Score a single game against a resident's goals + weak domains.
 * Returns { score, reasons } where reasons is an array of objects
 * we render as badges on the card. Each reason carries:
 *   - kind:   'goal' | 'goal-secondary' | 'weak'
 *   - goal:   the goal key (A-E)
 *   - domain: the domain that goal maps to (for label/color)
 */
export function scoreGame(game, { goals, weakSet }) {
  let score = 0;
  const reasons = [];
  const goalSet = new Set(goals || []);
  const gameGoals = game.goals || [];

  // Convert the weak DOMAIN set into the corresponding weak GOAL set
  // so we can reason entirely in goal-space below.
  const weakGoalSet = new Set();
  (weakSet || new Set()).forEach((domainKey) => {
    const goalKey = GOAL_BY_DOMAIN.get(domainKey);
    if (goalKey) weakGoalSet.add(goalKey);
  });

  const primaryGoal = gameGoals[0];
  const secondaryGoals = gameGoals.slice(1);

  // --- Goal matches ---
  if (primaryGoal && goalSet.has(primaryGoal)) {
    score += 2;
    reasons.push({
      kind: 'goal',
      goal: primaryGoal,
      domain: domainKeyFor(primaryGoal),
    });
  }
  secondaryGoals.forEach((goalKey) => {
    if (goalSet.has(goalKey)) {
      score += 1;
      reasons.push({
        kind: 'goal-secondary',
        goal: goalKey,
        domain: domainKeyFor(goalKey),
      });
    }
  });

  // --- Weak-domain boost ---
  // Only label as "weak area" if it's not already labeled as a goal
  // (avoids "this is your goal AND your weak area" double messaging).
  if (primaryGoal && weakGoalSet.has(primaryGoal) && !goalSet.has(primaryGoal)) {
    score += 1;
    reasons.push({
      kind: 'weak',
      goal: primaryGoal,
      domain: domainKeyFor(primaryGoal),
    });
  } else {
    // Half boost for a secondary-only weak match (first one found).
    const weakSecondary = secondaryGoals.find(
      (g) => weakGoalSet.has(g) && !goalSet.has(g)
    );
    if (weakSecondary) {
      score += 0.5;
      reasons.push({
        kind: 'weak',
        goal: weakSecondary,
        domain: domainKeyFor(weakSecondary),
      });
    }
  }

  return { score, reasons };
}

// Small helper: the domain KEY a goal maps to (or undefined).
function domainKeyFor(goalKey) {
  const d = domainForGoal(goalKey);
  return d ? d.key : undefined;
}

/**
 * Build today's recommended game list for a resident.
 *
 * @param {object} args
 *   @param {string} args.level       resident's level key
 *   @param {string[]} args.goals     selected goal keys (A-E)
 *   @param {object} args.breakdown   per-domain points (or null)
 *   @param {string} args.accountId   used as rotation seed
 *   @param {Date}   args.today       defaults to new Date()
 *   @param {number} args.count       how many to return (default 5)
 *
 * @returns {Array<{game, score, reasons, mode}>}
 */
export function recommendForResident({
  level,
  goals,
  breakdown,
  accountId,
  today = new Date(),
  count = 5,
}) {
  // 1. Filter to games available at this level.
  const eligible = gamesForLevel(level);

  // 2. Score each one.
  const weakSet = weakDomains(breakdown);
  const scored = eligible.map((game) => {
    const { score, reasons } = scoreGame(game, { goals, weakSet });
    return {
      game,
      score,
      reasons,
      // gamesForLevel attaches the mode already (null for single-mode games)
      mode: game.mode,
    };
  });

  // 3. Sort by score desc, with catalog order as a stable tiebreak.
  //    GAMES_LIST is the curated ordering.
  const catalogIndex = new Map(GAMES_LIST.map((g, i) => [g.id, i]));
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return catalogIndex.get(a.game.id) - catalogIndex.get(b.game.id);
  });

  // 4. Daily rotation: take top (count * 1.5), shuffle with today's
  //    seed, take top `count` from the shuffled result. This keeps
  //    the highest-scoring games consistently surfaced while
  //    rotating among the middle tier.
  //
  //    Hard floor: never rotate the top game — always show it.
  const poolSize = Math.min(scored.length, Math.ceil(count * 1.5));
  const pool = scored.slice(0, poolSize);
  const [topPick, ...rest] = pool;
  const rotated = rotateWithSeed(rest, dailySeed(accountId, today));
  const final = [topPick, ...rotated].slice(0, count);

  return final.filter(Boolean);
}

/**
 * Produce a numeric seed from accountId + today's date. Same
 * resident sees the same recommendations all day; different
 * residents see different rotations.
 */
function dailySeed(accountId, today) {
  const dateKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const combined = `${accountId || ''}|${dateKey}`;
  // djb2 hash — fast and good enough for this purpose
  let hash = 5381;
  for (let i = 0; i < combined.length; i++) {
    hash = ((hash << 5) + hash) ^ combined.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Deterministic Fisher–Yates shuffle using a seed. We don't need
 * cryptographic randomness — just stable per-day output.
 */
function rotateWithSeed(arr, seed) {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;  // LCG step
    const j = s % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
